import React, { useState, useEffect, useRef } from "react";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils/StringUtils";
import { Label } from "../Label";
import {
  SwitchContainer,
  ToggleContainer,
  LabelContainer,
  ToggleInput,
  ToggleCircle,
  ToggleLabel,
} from "../../Styles/Switch/Containers";

export interface Props {
  id: string;
  dataTestId: string;
  label: React.ComponentProps<typeof Label>;
  disabled?: boolean;
  viewOnly?: boolean;
  initiallyChecked?: boolean;
  classes?: string;
  smallLabelFont?: boolean;
  onChange?: (checked: boolean) => void;
}

const setToggleClass = (isChecked: boolean, screenSize: string) => {
  const classList = {
    defaultClass: "ons-switch-toggle",
    optionalClasses: [
      isChecked ? null : "ons-grid--reverse",
      screenSize === "regular" ? "ons-u-ml-m" : null,
    ],
  };

  const className = concatStringFromClassesArrayList(classList);
  return className;
};

export const Switch = (props: Props) => {
  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const [isChecked, setIsChecked] = useState(
    props.initiallyChecked ? true : false
  );

  const [screenSize, setScreenSize] = useState("regular");

  const updateScreenSize = () => {
    if (window.innerWidth < 500) {
      setScreenSize("small");
    } else {
      setScreenSize("regular");
    }
  };

  useEffect(() => {
    window.addEventListener("resize", updateScreenSize);
    updateScreenSize();

    return () => {
      window.removeEventListener("resize", updateScreenSize);
    };
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  const toggle = () => {
    if (!props.disabled && !props.viewOnly) {
      const newCheckedState = !isChecked;
      setIsChecked(newCheckedState);
      if (props.onChange) {
        props.onChange(newCheckedState);
      }
    }
  };

  const handleClick = () => {
    toggle();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const classList = {
    defaultClass: "ons-switch ons-u-pt-s ons-u-pb-s",
    optionalClasses: [
      props.classes ? props.classes : null,
      props.disabled ? "switch-disabled" : null,
      props.viewOnly ? "switch-view-only" : null,
    ],
  };

  const className = concatStringFromClassesArrayList(classList);

  const hasDescription = (): boolean => {
    return props.label.description ? true : false;
  };

  const labelClassList = {
    optionalClasses: [
      props.label.classes || null,
      props.smallLabelFont ? "ons-u-fs-r--b" : "ons-u-fs-m",
      !hasDescription() && screenSize === "regular" ? "ons-u-mb-no" : null,
    ],
  };

  const labelClassName = concatStringFromClassesArrayList(labelClassList);

  return (
    <SwitchContainer
      $checked={isChecked}
      $screenSize={screenSize}
      $hasDescription={hasDescription()}
      id={`${sanitisedId}-switch-container`}
      data-testid={`${sanitisedDataTestId}-switch-container`}
      className={className}
    >
      <LabelContainer
        id={`${sanitisedId}-switch-label-container`}
        data-testid={`${sanitisedDataTestId}-switch-label-container`}
      >
        {props.label && props.label.text && (
          <Label
            for={sanitisedId}
            id={`${sanitisedId}-switch-label`}
            dataTestId={`${sanitisedDataTestId}-switch`}
            text={props.label.text}
            classes={labelClassName}
            description={props.label.description}
            attributes={props.label.attributes}
            accessiblePlaceholder={props.label.accessiblePlaceholder}
          />
        )}
      </LabelContainer>
      <ToggleContainer
        id={`${sanitisedId}-switch-toggle-container`}
        data-testid={`${sanitisedDataTestId}-switch-toggle-container`}
        className={setToggleClass(isChecked, screenSize)}
        $checked={isChecked}
        $disabled={props.disabled}
        $viewOnly={props.viewOnly}
        onClick={handleClick}
        role="switch"
        aria-checked={isChecked}
        onKeyDown={handleKeyDown}
      >
        <ToggleInput
          ref={inputRef}
          type="checkbox"
          id={sanitisedId}
          data-testid={sanitisedDataTestId}
          checked={isChecked}
          onChange={toggle}
          disabled={props.disabled || props.viewOnly}
          aria-checked={isChecked}
          className="ons-switch-input ons-u-vh"
        />
        <ToggleLabel $checked={isChecked} aria-hidden="true">
          {isChecked ? "On" : "Off"}
        </ToggleLabel>
        <ToggleCircle $checked={isChecked}></ToggleCircle>
      </ToggleContainer>
    </SwitchContainer>
  );
};
